import React, { useState, useEffect } from 'react';
import BusinessSettingsForm from '../../components/BusinessSettingsForm';
import { getTaxRates, TaxRate, deleteTaxRate } from '../../lib/tax-rates';
import { useBusiness } from '../../contexts/BusinessContext';
import TaxRateModal from '../../components/TaxRateModal';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';
import { Plus, Pencil, Trash2 } from 'lucide-react';

export default function Business() {
  const { selectedBusiness } = useBusiness();
  const [taxRates, setTaxRates] = useState<TaxRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isTaxRateModalOpen, setIsTaxRateModalOpen] = useState(false);
  const [selectedTaxRate, setSelectedTaxRate] = useState<TaxRate | null>(null);
  const [taxRateModalMode, setTaxRateModalMode] = useState<'create' | 'edit'>('create');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [taxRateToDelete, setTaxRateToDelete] = useState<TaxRate | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const loadTaxRates = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      const data = await getTaxRates(selectedBusiness.id);
      setTaxRates(data);
      setError(null);
    } catch (err) {
      console.error('Error loading tax rates:', err);
      setError(err instanceof Error ? err.message : 'Failed to load tax rates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTaxRates();
  }, [selectedBusiness?.id]);

  const handleEditTaxRate = (taxRate: TaxRate) => {
    setSelectedTaxRate(taxRate);
    setTaxRateModalMode('edit');
    setIsTaxRateModalOpen(true);
  };

  const handleAddTaxRate = () => {
    setSelectedTaxRate(null);
    setTaxRateModalMode('create');
    setIsTaxRateModalOpen(true);
  };

  const handleDeleteTaxRate = (taxRate: TaxRate) => {
    setTaxRateToDelete(taxRate);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteTaxRate = async () => {
    if (!taxRateToDelete) return;

    try {
      setDeleteLoading(true);
      await deleteTaxRate(taxRateToDelete.id);
      await loadTaxRates();
      setIsDeleteModalOpen(false);
      setTaxRateToDelete(null);
    } catch (err) {
      console.error('Error deleting tax rate:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete tax rate');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen bg-gray-100 p-2 sm:p-4"
      style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}
    >
      <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6 pb-24">
        {/* Business Information Section */}
        <div className="bg-gray-50 rounded-2xl shadow-lg border border-gray-200/50 overflow-hidden">
          <div className="px-4 sm:px-8 py-4 sm:py-6 border-b border-gray-200/50">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 tracking-tight">Business Information</h2>
          </div>
          <div className="p-4 sm:p-8">
            <BusinessSettingsForm />
          </div>
        </div>

        {/* Tax Rates Section */}
        <div className="bg-gray-50 rounded-2xl shadow-lg border border-gray-200/50 overflow-hidden">
          <div className="px-4 sm:px-8 py-4 sm:py-6 border-b border-gray-200/50 flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 tracking-tight">Tax Rates</h2>
            <button
              onClick={handleAddTaxRate}
              className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2.5 sm:py-2 text-sm font-semibold text-white bg-purple-700 rounded-xl hover:bg-purple-800 transition-all duration-200 active:scale-95 shadow-sm"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Tax Rate
            </button>
          </div>
          <div className="divide-y divide-gray-200/50">
            {loading ? (
              <div className="p-6 sm:p-8 text-center">
                <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-purple-600 mx-auto"></div>
                <p className="mt-3 sm:mt-4 text-sm sm:text-base text-gray-600">Loading tax rates...</p>
              </div>
            ) : error ? (
              <div className="p-4 sm:p-8">
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 sm:px-6 py-3 sm:py-4 rounded-xl text-sm sm:text-base">
                  {error}
                </div>
              </div>
            ) : taxRates.length === 0 ? (
              <div className="p-6 sm:p-8 text-center">
                <p className="text-sm sm:text-base text-gray-500">No tax rates configured</p>
                <button
                  onClick={handleAddTaxRate}
                  className="mt-3 sm:mt-4 text-sm sm:text-base text-purple-600 hover:text-purple-700 font-medium"
                >
                  Add your first tax rate
                </button>
              </div>
            ) : (
              taxRates.map((taxRate) => (
                <div key={taxRate.id} className="p-4 sm:p-6 hover:bg-gray-50 transition-colors duration-200">
                  {/* Mobile-optimized tax rate layout */}
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-3 sm:space-y-0">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                        <h3 className="text-sm sm:text-base font-semibold text-gray-900">{taxRate.name}</h3>
                        {taxRate.is_default && (
                          <span className="px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium rounded-full bg-blue-100 text-blue-700 whitespace-nowrap">
                            Default
                          </span>
                        )}
                        {taxRate.is_compound && (
                          <span className="px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium rounded-full bg-purple-100 text-purple-700 whitespace-nowrap">
                            Compound
                          </span>
                        )}
                      </div>
                      {taxRate.description && (
                        <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600 line-clamp-2">{taxRate.description}</p>
                      )}
                      <div className="mt-2 text-sm sm:text-base font-semibold text-gray-900">
                        Rate: {taxRate.rate}%
                      </div>
                    </div>
                    {/* Mobile-optimized action buttons */}
                    <div className="flex items-center justify-end space-x-2 sm:ml-4">
                      <button
                        onClick={() => handleEditTaxRate(taxRate)}
                        className="p-2.5 sm:p-2 text-gray-400 hover:text-gray-600 rounded-xl hover:bg-gray-100 transition-all duration-200 active:scale-95 touch-manipulation"
                      >
                        <Pencil className="h-4 w-4 sm:h-5 sm:w-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteTaxRate(taxRate)}
                        className="p-2.5 sm:p-2 text-red-400 hover:text-red-600 rounded-xl hover:bg-red-50 transition-all duration-200 active:scale-95 touch-manipulation"
                      >
                        <Trash2 className="h-4 w-4 sm:h-5 sm:w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <TaxRateModal
          isOpen={isTaxRateModalOpen}
          onClose={() => {
            setIsTaxRateModalOpen(false);
            setSelectedTaxRate(null);
          }}
          onSuccess={() => {
            setIsTaxRateModalOpen(false);
            setSelectedTaxRate(null);
            loadTaxRates();
          }}
          businessId={selectedBusiness?.id}
          taxRate={selectedTaxRate}
          mode={taxRateModalMode}
        />

        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setTaxRateToDelete(null);
          }}
          onConfirm={confirmDeleteTaxRate}
          title="Delete Tax Rate"
          message={`Are you sure you want to delete the "${taxRateToDelete?.name}" tax rate? This action cannot be undone.`}
          loading={deleteLoading}
        />
      </div>
    </div>
  );
}